import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Upload, Download, Files, Link2, AlertCircle, CheckCircle, AlertTriangle, Search, Users, Info, RefreshCw, Layers } from "lucide-react";

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "-";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
};

export const ReferenceXMLImport = ({
  mainFileRef, tdsRefFileRef, vchMasterFileRef,
  handleMainFileUploadRef, handleTdsRefUpload, handleVchMasterUpload,
  mainFileData, tdsRefData, vchTypeMaster,
  matchResult, uploading, uploadMatchedTransactions,
  conflicts: conflictsFromProps,
  skippedEntries = [],
  panMismatches = [],
  amountMismatches = [],
  missingPartyEntries = [],
  setPartySearchModal, setVchTypeMaster
}) => {
  const conflicts = conflictsFromProps || [];
  return (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-blue-900">
                <Link2 className="w-5 h-5" />
                Import with TDS Ledger Reference (Recommended)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-100 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Why use this method?</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Matches TDS amounts by <strong>voucher number</strong> - 100% accurate</li>
                  <li>Resolves GST vs TDS confusion in columnar exports</li>
                  <li>Works with your existing XML exports - no TDL needed</li>
                </ul>
              </div>
              
              {/* Optional: Voucher Type Master */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-amber-600 font-semibold">Optional</span>
                  <h4 className="font-semibold text-slate-900">Voucher Type Master</h4>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Upload voucher type list to auto-map custom types (e.g., "Purchases - Hosur Road" → Purchase)
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls,.xml"
                  ref={vchMasterFileRef}
                  onChange={handleVchMasterUpload}
                  className="hidden"
                  id="vch-master-upload"
                />
                <div className="flex items-center gap-3">
                  <label htmlFor="vch-master-upload">
                    <Button 
                      as="span"
                      variant={vchTypeMaster ? "outline" : "secondary"}
                      size="sm"
                      className="cursor-pointer"
                      onClick={() => vchMasterFileRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {vchTypeMaster ? `✓ ${vchTypeMaster.voucherTypes.length} types loaded` : 'Upload Voucher Type Master (XML/Excel)'}
                    </Button>
                  </label>
                  {vchTypeMaster && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setVchTypeMaster(null)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {vchTypeMaster && (
                  <div className="mt-3 max-h-32 overflow-y-auto">
                    <p className="text-xs text-slate-500 mb-1">Detected mappings:</p>
                    <div className="flex flex-wrap gap-1">
                      {vchTypeMaster.voucherTypes.slice(0, 15).map((vt) => (
                        <span key={`${vt.name}-${vt.parent}`} className="text-xs bg-white px-2 py-0.5 rounded border">
                          {vt.name} → <span className="text-blue-600">{vt.parent}</span>
                        </span>
                      ))}
                      {vchTypeMaster.voucherTypes.length > 15 && (
                        <span className="text-xs text-slate-500">+{vchTypeMaster.voucherTypes.length - 15} more</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Step 1: TDS Reference File */}
                <div className="space-y-3 p-4 bg-white rounded-lg border">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    <h4 className="font-semibold text-slate-900">Upload TDS Ledger XML(s)</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    Export TDS ledgers (e.g., "TDS ON RENT 194I") in <strong>non-columnar</strong> view as XML. <strong>Multiple files supported.</strong>
                  </p>
                  <input
                    type="file"
                    accept=".xml"
                    ref={tdsRefFileRef}
                    onChange={handleTdsRefUpload}
                    className="hidden"
                    id="tds-ref-upload"
                    multiple
                  />
                  <label htmlFor="tds-ref-upload">
                    <Button 
                      as="span"
                      variant={tdsRefData ? "outline" : "default"}
                      className="cursor-pointer w-full"
                      onClick={() => tdsRefFileRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {tdsRefData ? `✓ ${tdsRefData.fileCount || 1} file(s) loaded` : 'Upload TDS Ledger XML(s)'}
                    </Button>
                  </label>
                  {tdsRefData && (
                    <div className="text-sm space-y-1">
                      <p className="text-green-600">✓ {tdsRefData.entries.length} TDS entries from {tdsRefData.fileCount || 1} file(s)</p>
                      {tdsRefData.sections?.length > 0 && (
                        <p className="text-blue-600">Sections: {tdsRefData.sections.join(', ')}</p>
                      )}
                      {tdsRefData.conflicts?.length > 0 && (
                        <p className="text-red-600 font-medium">⚠️ {tdsRefData.conflicts.length} conflicts found!</p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Step 2: Main File */}
                <div className="space-y-3 p-4 bg-white rounded-lg border">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    <h4 className="font-semibold text-slate-900">Upload Main Columnar XML(s)</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    Export the same TDS ledgers in <strong>columnar</strong> view (F5) as XML. <strong>Multiple files supported.</strong>
                  </p>
                  <input
                    type="file"
                    accept=".xml,.txt,.csv"
                    ref={mainFileRef}
                    onChange={handleMainFileUploadRef}
                    className="hidden"
                    id="main-file-upload"
                    disabled={!tdsRefData}
                    multiple
                  />
                  <label htmlFor="main-file-upload">
                    <Button 
                      as="span"
                      variant={mainFileData ? "outline" : "default"}
                      className={`cursor-pointer w-full ${!tdsRefData ? 'opacity-50' : ''}`}
                      onClick={() => tdsRefData && mainFileRef.current?.click()}
                      disabled={!tdsRefData}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {mainFileData ? `✓ ${mainFileData.fileCount || 1} file(s) loaded` : 'Upload Columnar XML(s)'}
                    </Button>
                  </label>
                  {!tdsRefData && (
                    <p className="text-sm text-slate-500">Upload TDS reference first</p>
                  )}
                  {mainFileData && matchResult && (
                    <p className="text-sm text-green-600">
                      ✓ {matchResult.total} transactions from {mainFileData.fileCount || 1} file(s) ({matchResult.matched} matched)
                    </p>
                  )}
                </div>
              </div>
              
              {/* Show Conflicts Warning */}
              {tdsRefData?.conflicts?.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Conflicting TDS Amounts Detected ({tdsRefData.conflicts.length})
                  </h4>
                  <p className="text-sm text-red-700 mb-3">
                    The same voucher type + number appears in multiple files with different TDS amounts. 
                    <strong> The LAST file's value will be used.</strong> Consider uploading only the correct file.
                  </p>
                  <div className="overflow-x-auto max-h-48 overflow-y-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-red-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2 border-b border-red-200">Vch Type</th>
                          <th className="text-left p-2 border-b border-red-200">Vch No</th>
                          <th className="text-left p-2 border-b border-red-200">First File</th>
                          <th className="text-right p-2 border-b border-red-200">First TDS</th>
                          <th className="text-left p-2 border-b border-red-200">Second File</th>
                          <th className="text-right p-2 border-b border-red-200">Second TDS (Used)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tdsRefData.conflicts.map((conflict) => (
                          <tr key={`${conflict.voucher_no}-${conflict.voucher_type}`} className="hover:bg-red-100/50">
                            <td className="p-2 border-b border-red-100 text-xs font-medium">{conflict.voucher_type || '-'}</td>
                            <td className="p-2 border-b border-red-100 font-mono font-bold">{conflict.voucher_no}</td>
                            <td className="p-2 border-b border-red-100 text-xs">{conflict.existingFile}</td>
                            <td className="p-2 border-b border-red-100 text-right font-mono line-through text-red-400">
                              ₹{conflict.existingAmount?.toLocaleString('en-IN')}
                            </td>
                            <td className="p-2 border-b border-red-100 text-xs">{conflict.newFile}</td>
                            <td className="p-2 border-b border-red-100 text-right font-mono text-red-700 font-bold">
                              ₹{conflict.newAmount?.toLocaleString('en-IN')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-red-600 mt-2">
                    💡 <strong>Tip:</strong> If the "Second TDS" values are wrong, remove that file and re-upload only the correct files.
                  </p>
                </div>
              )}
              
              {/* Match Result & Upload */}
              {matchResult && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-green-900">Ready to Import</h4>
                      <p className="text-sm text-green-700">
                        {matchResult.total} transactions • {matchResult.matched} matched with TDS reference • {matchResult.unmatched} unmatched
                      </p>
                    </div>
                    <Button 
                      onClick={uploadMatchedTransactions}
                      disabled={uploading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {uploading ? (
                        <>
                          <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Import Transactions
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Show Skipped Entries Warning */}
              {skippedEntries.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-300">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Skipped Entries ({skippedEntries.length}) - Missing Voucher Number
                  </h4>
                  <p className="text-sm text-amber-700 mb-3">
                    The following entries were skipped because they have no Voucher No. or Voucher Ref in the XML. Please verify in Tally.
                  </p>
                  <div className="overflow-x-auto max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-amber-900">Date</th>
                          <th className="px-3 py-2 text-left text-amber-900">Party Name</th>
                          <th className="px-3 py-2 text-left text-amber-900">Voucher Type</th>
                          <th className="px-3 py-2 text-right text-amber-900">TDS Amount</th>
                          <th className="px-3 py-2 text-right text-amber-900">Gross Amount</th>
                          <th className="px-3 py-2 text-left text-amber-900">File</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skippedEntries.map((entry) => (
                          <tr key={`${entry.party_name}-${entry.date}-${entry.voucher_type}-${entry.tds_amount}`} className="border-b border-amber-200 hover:bg-amber-100">
                            <td className="px-3 py-2">{entry.date || '-'}</td>
                            <td className="px-3 py-2 font-medium">{entry.party_name}</td>
                            <td className="px-3 py-2">{entry.voucher_type || '-'}</td>
                            <td className="px-3 py-2 text-right font-mono">₹{(entry.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right font-mono">₹{(entry.gross_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-xs text-amber-600">{entry.filename}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Show PAN Mismatch Warning */}
              {panMismatches.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-300">
                  <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    PAN Mismatch Alert ({panMismatches.length}) - XML vs Database
                  </h4>
                  <p className="text-sm text-red-700 mb-3">
                    The following transactions have a different PAN in the XML compared to the stored party details. Please verify and update if needed.
                  </p>
                  <div className="overflow-x-auto max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-red-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-red-900">Voucher No</th>
                          <th className="px-3 py-2 text-left text-red-900">Party Name</th>
                          <th className="px-3 py-2 text-left text-red-900">XML PAN</th>
                          <th className="px-3 py-2 text-left text-red-900">DB PAN</th>
                          <th className="px-3 py-2 text-right text-red-900">TDS Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {panMismatches.map((entry) => (
                          <tr key={`${entry.voucher_no}-${entry.party_name}-${entry.xml_pan}`} className="border-b border-red-200 hover:bg-red-100">
                            <td className="px-3 py-2 font-mono text-xs">{entry.voucher_no || '-'}</td>
                            <td className="px-3 py-2 font-medium">{entry.party_name}</td>
                            <td className="px-3 py-2 font-mono text-red-700 font-bold">{entry.xml_pan}</td>
                            <td className="px-3 py-2 font-mono text-blue-700">{entry.db_pan}</td>
                            <td className="px-3 py-2 text-right font-mono">₹{(entry.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Show Missing Party/PAN Warning with Search Option */}
              {missingPartyEntries.length > 0 && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-300">
                  <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Missing Party Info ({missingPartyEntries.length}) - Assign from Existing Parties
                  </h4>
                  <p className="text-sm text-purple-700 mb-3">
                    These transactions have missing PAN or party name. Click "Search" to assign from existing parties.
                  </p>
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-purple-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-purple-900">Voucher No</th>
                          <th className="px-3 py-2 text-left text-purple-900">Party Name</th>
                          <th className="px-3 py-2 text-left text-purple-900">PAN</th>
                          <th className="px-3 py-2 text-right text-purple-900">TDS Amount</th>
                          <th className="px-3 py-2 text-left text-purple-900">Missing</th>
                          <th className="px-3 py-2 text-center text-purple-900">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missingPartyEntries.map((entry) => (
                          <tr key={`${entry.voucher_no}-${entry.party_name}-${entry.tds_amount}`} className="border-b border-purple-200 hover:bg-purple-100">
                            <td className="px-3 py-2 font-mono text-xs">{entry.voucher_no || '-'}</td>
                            <td className="px-3 py-2 font-medium">{entry.party_name}</td>
                            <td className="px-3 py-2 font-mono">{entry.pan || <span className="text-red-500 italic">Missing</span>}</td>
                            <td className="px-3 py-2 text-right font-mono">₹{(entry.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2">
                              {entry.missing_pan && <span className="text-xs bg-red-100 text-red-700 px-1 rounded mr-1">PAN</span>}
                              {entry.missing_name && <span className="text-xs bg-orange-100 text-orange-700 px-1 rounded">Name</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => setPartySearchModal({ open: true, txnIndex: entry.index, searchQuery: entry.party_name || '' })}
                              >
                                Search Party
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Show Amount Mismatch Warning (Amount=0 but TDS>0) */}
              {amountMismatches.length > 0 && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-300">
                  <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Amount Mismatch Alert ({amountMismatches.length}) - Zero Amount with TDS/TCS
                  </h4>
                  <p className="text-sm text-orange-700 mb-3">
                    These transactions have TDS/TCS amount but the base amount (expense/sale turnover) is zero. This may indicate missing data in the XML.
                  </p>
                  <div className="overflow-x-auto max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-orange-900">Voucher No</th>
                          <th className="px-3 py-2 text-left text-orange-900">Party Name</th>
                          <th className="px-3 py-2 text-left text-orange-900">Section</th>
                          <th className="px-3 py-2 text-right text-orange-900">Amount</th>
                          <th className="px-3 py-2 text-right text-orange-900">TDS/TCS</th>
                          <th className="px-3 py-2 text-left text-orange-900">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amountMismatches.map((entry) => (
                          <tr key={`${entry.voucher_no}-${entry.party_name}-${entry.amount}-${entry.tds_amount}`} className="border-b border-orange-200 hover:bg-orange-100">
                            <td className="px-3 py-2 font-mono text-xs">{entry.voucher_no || '-'}</td>
                            <td className="px-3 py-2 font-medium">{entry.party_name}</td>
                            <td className="px-3 py-2">{entry.section || '-'}</td>
                            <td className="px-3 py-2 text-right font-mono text-red-600 font-bold">
                              ₹{(entry.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-orange-700">
                              ₹{(entry.tds_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`text-xs px-2 py-1 rounded ${entry.is_tcs ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                {entry.is_tcs ? 'TCS' : 'TDS'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Show Sample Matched Transactions for Verification */}
              {matchResult && matchResult.matched > 0 && mainFileData?.transactions && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Sample Matched Transactions (First 10)
                  </h4>
                  <p className="text-sm text-green-700 mb-3">
                    Verify that TDS amounts look correct before importing.
                  </p>
                  <div className="overflow-x-auto max-h-60 overflow-y-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-green-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2 border-b border-green-200">Vch No</th>
                          <th className="text-left p-2 border-b border-green-200">Party Name</th>
                          <th className="text-right p-2 border-b border-green-200">Amount</th>
                          <th className="text-right p-2 border-b border-green-200">TDS Amt</th>
                          <th className="text-center p-2 border-b border-green-200">Matched?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mainFileData.transactions.slice(0, 10).map((txn) => (
                          <tr key={`${txn.voucher_no}-${txn.party_name}-${txn.amount}`} className="hover:bg-green-100/50">
                            <td className="p-2 border-b border-green-100 font-mono text-xs">{txn.voucher_no || '-'}</td>
                            <td className="p-2 border-b border-green-100">{txn.party_name || '-'}</td>
                            <td className="p-2 border-b border-green-100 text-right font-mono">
                              ₹{txn.amount?.toLocaleString('en-IN') || '0'}
                            </td>
                            <td className="p-2 border-b border-green-100 text-right font-mono text-green-700 font-medium">
                              ₹{txn.tds_amount?.toLocaleString('en-IN') || '0'}
                            </td>
                            <td className="p-2 border-b border-green-100 text-center">
                              {txn._matchedFromRef ? (
                                <span className="text-green-600 text-xs font-medium">✓ Yes</span>
                              ) : (
                                <span className="text-amber-600 text-xs font-medium">No</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Show Unmatched Transactions */}
              {matchResult && matchResult.unmatched > 0 && mainFileData?.unmatchedTransactions && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Unmatched Transactions ({matchResult.unmatched})
                  </h4>
                  <p className="text-sm text-amber-700 mb-3">
                    These transactions couldn't be matched with TDS reference by voucher number. TDS amounts may not be accurate.
                  </p>
                  <div className="overflow-x-auto max-h-60 overflow-y-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-amber-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2 border-b border-amber-200">Date</th>
                          <th className="text-left p-2 border-b border-amber-200">Vch No</th>
                          <th className="text-left p-2 border-b border-amber-200">Party Name</th>
                          <th className="text-right p-2 border-b border-amber-200">Amount</th>
                          <th className="text-right p-2 border-b border-amber-200">TDS Amt</th>
                          <th className="text-left p-2 border-b border-amber-200">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mainFileData.unmatchedTransactions.map((txn) => (
                          <tr key={`${txn.date}-${txn.voucher_no}-${txn.party_name}`} className="hover:bg-amber-100/50">
                            <td className="p-2 border-b border-amber-100 font-mono text-xs">{txn.date || '-'}</td>
                            <td className="p-2 border-b border-amber-100 font-mono text-xs">{txn.voucher_no || '-'}</td>
                            <td className="p-2 border-b border-amber-100">{txn.party_name || '-'}</td>
                            <td className="p-2 border-b border-amber-100 text-right font-mono">
                              {txn.amount ? `₹${txn.amount.toLocaleString('en-IN')}` : '-'}
                            </td>
                            <td className="p-2 border-b border-amber-100 text-right font-mono text-amber-700 font-medium">
                              {txn.tds_amount ? `₹${txn.tds_amount.toLocaleString('en-IN')}` : '-'}
                            </td>
                            <td className="p-2 border-b border-amber-100 text-xs text-amber-600">
                              {!txn.voucher_no ? 'No voucher number' : 'Voucher not in TDS reference'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-700">
                  <strong>How to export:</strong> In Tally, go to the TDS ledger → Press Ctrl+E → Select XML format. 
                  Do this twice: once in normal view (for reference) and once in columnar view F5 (for party details).
                </p>
              </div>
            </CardContent>
          </Card>

  );
};
